/**
 * GoalSuggestDialog — suggest a goal to the practice patient (Dave, 23 Sep).
 *
 * Works in text and in a live voice call. The fields match real client goals
 * (server/models/Goal.js): a description and a type, one-off or habit.
 * The patient answers in character and may accept, hesitate, negotiate or
 * decline, so this is practice at proposing goals, not a form that always
 * succeeds.
 */

import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';

export const MAX_GOAL_LENGTH = 500;

export default function GoalSuggestDialog({ open, onClose, onSubmit, isSubmitting = false, inCall = false }) {
  const [description, setDescription] = useState('');
  const [goalType, setGoalType] = useState('one_off');
  const [error, setError] = useState('');

  // Start clean each time it opens.
  useEffect(() => {
    if (open) {
      setDescription('');
      setGoalType('one_off');
      setError('');
    }
  }, [open]);

  const trimmed = description.trim();
  const tooLong = description.length > MAX_GOAL_LENGTH;
  const canSubmit = !!trimmed && !tooLong && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError('');
    try {
      await onSubmit({ description: trimmed, goalType });
    } catch (err) {
      setError(err?.message || 'Could not suggest the goal. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Suggest a Goal</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {inCall
            ? 'The patient will respond out loud, in character. They may agree, hesitate, or push back.'
            : 'The patient will respond in character. They may agree, hesitate, or push back.'}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label="Goal"
          placeholder="e.g. Walk for ten minutes each morning"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={tooLong}
          helperText={tooLong
            ? `Keep it under ${MAX_GOAL_LENGTH} characters`
            : `${description.length}/${MAX_GOAL_LENGTH}`}
          inputProps={{ 'data-testid': 'goal-description' }}
          sx={{ mb: 2 }}
        />
        <TextField
          select
          fullWidth
          label="Type"
          value={goalType}
          onChange={(e) => setGoalType(e.target.value)}
          SelectProps={{ SelectDisplayProps: { 'data-testid': 'goal-type' } }}
        >
          <MenuItem value="one_off">One-off task</MenuItem>
          <MenuItem value="habit">Habit</MenuItem>
        </TextField>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="goal-submit"
        >
          {isSubmitting ? 'Suggesting…' : 'Suggest Goal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
